/**
 * Admin Blogs Management
 * Full CRUD with AI generation, filters, and search
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { adminApiGet, adminApiDelete, adminApiPatch } from '../../utils/adminApi';
import toast from '../../utils/toast';

import {
  Calendar,
  CircleNotch,
  Clock,
  Eye,
  EyeSlash,
  FileText,
  Funnel,
  MagnifyingGlass,
  PencilSimple,
  Plus,
  Sparkle,
  Tag,
  Trash,
  TrendUp
} from '@phosphor-icons/react';
const BlogsManagement = () => {
  const navigate = useNavigate();
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortBy, setSortBy] = useState('created_desc');

  useEffect(() => {
    fetchBlogs();
  }, []);

  const fetchBlogs = async () => {
    try {
      setLoading(true);
      const data = await adminApiGet('/api/admin/blogs');
      setBlogs(data || []);
    } catch (error) {
      console.error('Error fetching blogs:', error);
      toast.error('Failed to load blogs');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id, title) => {
    if (!window.confirm(`Delete "${title}"? This action cannot be undone.`)) return;

    try {
      await adminApiDelete(`/api/admin/blogs/${id}`);
      toast.success('Blog deleted successfully');
      fetchBlogs();
    } catch (error) {
      console.error('Error deleting blog:', error);
      toast.error(error.message || 'Failed to delete blog');
    }
  };

  const togglePublish = async (id, currentStatus) => {
    try {
      await adminApiPatch(`/api/admin/blogs/${id}`, { published: !currentStatus });
      toast.success(currentStatus ? 'Blog unpublished' : 'Blog published');
      fetchBlogs();
    } catch (error) {
      console.error('Error updating blog:', error);
      toast.error(error.message || 'Failed to update blog');
    }
  };

  // Funnel and sort blogs
  const filteredBlogs = blogs
    .filter(blog => {
      const matchesSearch = blog.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                           blog.excerpt?.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesStatus = statusFilter === 'all' ||
                           (statusFilter === 'published' && blog.published) ||
                           (statusFilter === 'draft' && !blog.published);
      return matchesSearch && matchesStatus;
    })
    .sort((a, b) => {
      if (sortBy === 'created_desc') return new Date(b.created_at) - new Date(a.created_at);
      if (sortBy === 'created_asc') return new Date(a.created_at) - new Date(b.created_at);
      if (sortBy === 'title_asc') return (a.title || '').localeCompare(b.title || '');
      return 0;
    });

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="h-12 bg-white/10 rounded animate-pulse w-64"></div>
        <div className="grid grid-cols-1 gap-4">
          {[1, 2, 3].map(i => (
            <div key={i} className="h-32 bg-white/10 rounded-xl animate-pulse"></div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2">Blog Management</h1>
          <p className="text-gray-400">Create, edit, and publish blog posts</p>
        </div>
        <button
          onClick={() => navigate('/admin/blogs/new')}
          className="px-4 py-2 bg-gradient-to-r from-[#6366F1] to-[#EC4899] rounded-lg hover:opacity-90 transition-opacity flex items-center gap-2 font-semibold"
        >
          <Plus size={18} />
          New Blog Post
        </button>
      </div>

      {/* Filters & MagnifyingGlass */}
      <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* MagnifyingGlass */}
          <div className="relative">
            <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search blogs..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-white/5 border border-white/10 rounded-lg text-sm focus:outline-none focus:border-[#6366F1]/50"
            />
          </div>

          {/* Status Funnel */}
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-sm focus:outline-none focus:border-[#6366F1]/50"
          >
            <option value="all">All Status</option>
            <option value="published">Published</option>
            <option value="draft">Drafts</option>
          </select>

          {/* Sort */}
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-sm focus:outline-none focus:border-[#6366F1]/50"
          >
            <option value="created_desc">Newest First</option>
            <option value="created_asc">Oldest First</option>
            <option value="title_asc">Title A-Z</option>
          </select>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
              <FileText size={20} className="text-blue-400" />
            </div>
            <div>
              <p className="text-xs text-gray-400">Total Blogs</p>
              <p className="text-2xl font-bold text-white">{blogs.length}</p>
            </div>
          </div>
        </div>

        <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center">
              <Eye size={20} className="text-green-400" />
            </div>
            <div>
              <p className="text-xs text-gray-400">Published</p>
              <p className="text-2xl font-bold text-white">
                {blogs.filter(b => b.published).length}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-yellow-500/20 flex items-center justify-center">
              <EyeSlash size={20} className="text-yellow-400" />
            </div>
            <div>
              <p className="text-xs text-gray-400">Drafts</p>
              <p className="text-2xl font-bold text-white">
                {blogs.filter(b => !b.published).length}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Blogs List */}
      {filteredBlogs.length === 0 ? (
        <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-12 text-center">
          <FileText size={48} className="text-gray-500 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-white mb-2">
            {searchQuery || statusFilter !== 'all' ? 'No blogs found' : 'No blogs yet'}
          </h3>
          <p className="text-gray-400 mb-4">
            {searchQuery || statusFilter !== 'all'
              ? 'Try adjusting your filters'
              : 'Create your first blog post to get started'}
          </p>
          {!searchQuery && statusFilter === 'all' && (
            <button
              onClick={() => navigate('/admin/blogs/new')}
              className="px-4 py-2 bg-[#6366F1] hover:bg-[#5558E3] rounded-lg transition-colors inline-flex items-center gap-2"
            >
              <Plus size={18} />
              Create First Blog
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          {filteredBlogs.map((blog) => (
            <motion.div
              key={blog.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6 hover:border-white/20 transition-all"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-lg font-semibold text-white">{blog.title}</h3>
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                      blog.published
                        ? 'bg-green-500/20 text-green-400'
                        : 'bg-yellow-500/20 text-yellow-400'
                    }`}>
                      {blog.published ? 'Published' : 'Draft'}
                    </span>
                  </div>

                  {blog.excerpt && (
                    <p className="text-sm text-gray-400 mb-3 line-clamp-2">{blog.excerpt}</p>
                  )}

                  <div className="flex items-center gap-4 text-xs text-gray-500">
                    <div className="flex items-center gap-1">
                      <Calendar size={14} />
                      {new Date(blog.created_at).toLocaleDateString()}
                    </div>
                    {blog.category && (
                      <div className="flex items-center gap-1">
                        <Tag size={14} />
                        {blog.category}
                      </div>
                    )}
                    {blog.read_time && (
                      <div className="flex items-center gap-1">
                        <Clock size={14} />
                        {blog.read_time}
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-2 ml-4">
                  <button
                    onClick={() => togglePublish(blog.id, blog.published)}
                    className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                    title={blog.published ? 'Unpublish' : 'Publish'}
                  >
                    {blog.published ? <EyeSlash size={18} /> : <Eye size={18} />}
                  </button>
                  <button
                    onClick={() => navigate(`/admin/blogs/edit/${blog.id}`)}
                    className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                    title="Edit"
                  >
                    <PencilSimple size={18} />
                  </button>
                  <button
                    onClick={() => handleDelete(blog.id, blog.title)}
                    className="p-2 hover:bg-red-500/20 text-red-400 rounded-lg transition-colors"
                    title="Delete"
                  >
                    <Trash size={18} />
                  </button>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
};

export default BlogsManagement;
